import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

/**
 * Layout-neutral background wrapper.
 * Nest around content (TitleSection, etc). Do not place between a Bootstrap
 * Row and its Col children — wrap the Row, or wrap content inside a Col.
 */
const Background = ({
  bgImage,
  backgroundColor,
  className,
  children,
  sections = [],
  renderSection,
}) => {
  const bg = bgImage?.src ? bgImage : null;
  const responsiveBgImage = useResponsiveImageUrl(bg?.src || "");
  const resolvedBgImage = bg ? responsiveBgImage : null;
  const pinToBottom = bg?.pin === "bottom";

  return (
    <Wrapper
      className={className}
      backgroundColor={backgroundColor}
      $bgImage={pinToBottom ? null : resolvedBgImage}
      $bgPosition={bg?.position}
      $bgSize={bg?.size}
      $hideBgOnMobile={Boolean(bg?.hideOnMobile)}
      $stackOnMobile={Boolean(bg?.stackOnMobile)}
      $pinToBottom={pinToBottom}
    >
      {pinToBottom && resolvedBgImage && (
        <PinnedImage
          src={resolvedBgImage}
          alt=""
          $size={bg.size}
          $clipBottom={bg.clipBottom || "22%"}
        />
      )}
      {children}
      {sections.map((section, index) =>
        renderSection ? renderSection(section, index) : null,
      )}
      {bg?.stackOnMobile && resolvedBgImage && (
        <StackedImage src={resolvedBgImage} alt={bg.alt || ""} />
      )}
    </Wrapper>
  );
};

Background.propTypes = {
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  sections: PropTypes.array,
  renderSection: PropTypes.func,
  bgImage: PropTypes.shape({
    src: PropTypes.string,
    position: PropTypes.string,
    size: PropTypes.string,
    hideOnMobile: PropTypes.bool,
    stackOnMobile: PropTypes.bool,
    pin: PropTypes.oneOf(["bottom"]),
    clipBottom: PropTypes.string,
    alt: PropTypes.string,
  }),
};

const Wrapper = styled.div`
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  background-color: ${(props) => props.backgroundColor || "transparent"};

  ${(props) =>
    (props.$bgImage || props.$pinToBottom) &&
    `
    position: relative;
    overflow: hidden;
  `}

  ${(props) =>
    props.$bgImage &&
    `
    background-image: url(${props.$bgImage});
    background-position: ${props.$bgPosition || "center"};
    background-size: ${props.$bgSize || "cover"};
    background-repeat: no-repeat;
  `}

  ${(props) =>
    props.$pinToBottom &&
    `
    > :not(img) {
      position: relative;
      z-index: 1;
    }

    @media screen and (min-width: 992px) {
      padding-bottom: 10rem;
    }
  `}

  @media screen and (max-width: 991px) {
    ${(props) =>
      (props.$stackOnMobile || props.$hideBgOnMobile) &&
      `
      background-image: none;
    `}
  }
`;

const PinnedImage = styled.img`
  display: none;

  @media screen and (min-width: 992px) {
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    width: ${(props) => props.$size || "28%"};
    height: auto;
    transform: translateY(${(props) => props.$clipBottom || "22%"});
    pointer-events: none;
    z-index: 0;
  }
`;

const StackedImage = styled.img`
  display: none;
  width: min(280px, 70%);
  height: auto;
  margin: 16px auto 0;
  object-fit: contain;

  @media screen and (max-width: 991px) {
    display: block;
  }
`;

export default Background;
